/**
 * GDL Tracking — event wiring
 *
 * Decides *when* events fire. All payload shaping lives in gdl-tracking-sdk.js.
 *
 * Currently wired:
 * - view_item    : PDP load + variant change
 * - add_to_cart  : cart diff after any /cart/add|change|update call, so PDP form,
 *                  quick add, configurator and quantity increases in the cart or
 *                  drawer are all covered by a single code path.
 *
 * List attribution: a container marked `data-track-list="<item_list_name>"` and
 * cards marked `data-track-product-id="<product id>"`. Clicking a card stores the
 * list name and index, which then travel with the product into view_item and
 * add_to_cart.
 *
 * To add a new event: add a track.* method in the SDK, then trigger it here.
 */
(function () {
  var SDK = window.TrackingSDK;
  if (!SDK) return;

  var track = SDK.track;
  var fired = new Set();

  // Guards against the same event firing twice for the same key within 5s
  // (re-renders, duplicated Swiper slides, double cart responses).
  function once(key) {
    if (fired.has(key)) return false;
    fired.add(key);
    setTimeout(function () {
      fired.delete(key);
    }, 5000);
    return true;
  }

  // =============================
  // VIEW_ITEM
  // =============================
  function variantPayload(product, variant, isExplicitVariant) {
    return {
      id: product.id,
      variant_id: isExplicitVariant ? variant.id : null,
      title: product.title,
      category: product.category,
      color: variant.color,
      sku: variant.sku,
      price: variant.price,
      compare_at_price: variant.compare_at_price,
      available: variant.available,
      inventory_quantity: variant.inventory_quantity,
      variant_title: variant.title,
      variants: product.variants,
    };
  }

  function trackPDP() {
    var T = window.__TRACKING__;
    if (!T || !T.product || !T.product.variants || !T.product.variants.length) return;

    var p = T.product;
    if (!once('view_item:' + p.id)) return;

    var urlVariantId = new URLSearchParams(location.search).get('variant');
    var selected =
      urlVariantId &&
      p.variants.find(function (v) {
        return String(v.id) === urlVariantId;
      });
    var variant =
      selected ||
      p.variants.find(function (v) {
        return v.available;
      }) ||
      p.variants[0];

    track.viewItem(variantPayload(p, variant, !!selected));
  }

  function trackVariantChange(event) {
    var T = window.__TRACKING__;
    if (!T || !T.product) return;

    var changed = event && event.data && event.data.variant;
    if (!changed) return;

    var p = T.product;
    // Prefer the Liquid-rendered variant: it carries sku, color and stock.
    var variant =
      p.variants.find(function (v) {
        return String(v.id) === String(changed.id);
      }) || {
        id: changed.id,
        sku: changed.sku,
        title: changed.title,
        color: '',
        price: changed.price,
        compare_at_price: changed.compare_at_price,
        available: changed.available,
        inventory_quantity: 0,
      };

    if (!once('view_item:' + p.id + ':' + variant.id)) return;

    track.viewItem(variantPayload(p, variant, true));
  }

  // =============================
  // LIST ATTRIBUTION  —  item_list_name / index
  //
  // Sections that only appear in one context hardcode data-track-list.
  // Generic carousels reused across pages set data-track-list="auto" and the
  // list name is derived from the page type here, so the mapping lives in one place.
  // =============================
  var PAGE_LIST_NAMES = {
    index: 'homepage',
    collection: 'category listing',
    search: 'search results',
    page: 'landing page',
  };

  function resolveListName(listEl) {
    var name = listEl.dataset.trackList || '';
    if (name !== 'auto') return name;

    var pageType = (window.__TRACKING__ && window.__TRACKING__.page && window.__TRACKING__.page.page_type) || '';
    return PAGE_LIST_NAMES[pageType] || 'other';
  }

  document.addEventListener('click', function (e) {
    var card = e.target.closest('[data-track-product-id]');
    if (!card) return;

    var listEl = card.closest('[data-track-list]');
    if (!listEl) return;

    var cards = Array.prototype.filter.call(
      listEl.querySelectorAll('[data-track-product-id]'),
      function (el) {
        return !el.closest('.swiper-slide-duplicate');
      }
    );

    var productId = card.dataset.trackProductId;
    var existing = SDK.Attribution.get(productId) || {};
    existing.item_list_name = resolveListName(listEl);
    existing.index = Math.max(cards.indexOf(card), 0);
    SDK.Attribution.set(productId, existing);
  });

  // =============================
  // ADD_TO_CART  —  cart diff
  // =============================
  var lastCart = null;

  function fetchCart() {
    return fetch(window.Shopify.routes.root + 'cart.js').then(function (r) {
      return r.json();
    });
  }

  function diffCarts(oldCart, newCart) {
    var added = [];
    if (!oldCart) return added;

    var oldMap = {};
    oldCart.items.forEach(function (i) {
      oldMap[i.key] = i;
    });

    newCart.items.forEach(function (i) {
      var oldQty = oldMap[i.key] ? oldMap[i.key].quantity : 0;
      if (i.quantity > oldQty) added.push({ item: i, qty: i.quantity - oldQty });
    });

    return added;
  }

  // /cart.js does not expose inventory_quantity, so pull it from the product JSON
  // for any variant we have not seen yet.
  function ensureStock(changes) {
    var handles = {};
    changes.forEach(function (c) {
      if (SDK.Attribution.get('vstock:' + c.item.variant_id) === null && c.item.handle) {
        handles[c.item.handle] = true;
      }
    });

    return Promise.all(
      Object.keys(handles).map(function (handle) {
        return fetch(window.Shopify.routes.root + 'products/' + handle + '.js')
          .then(function (r) {
            return r.json();
          })
          .then(function (p) {
            (p.variants || []).forEach(function (v) {
              SDK.Attribution.set('vstock:' + v.id, v.inventory_quantity || 0);
            });
          })
          .catch(function () {});
      })
    );
  }

  function handleCartUpdate(newCart) {
    var changes = diffCarts(lastCart, newCart);
    lastCart = newCart;
    if (!changes.length) return;

    ensureStock(changes).then(function () {
      changes.forEach(function (c) {
        if (!once('add_to_cart:' + c.item.key + ':' + c.qty)) return;
        track.addToCart(c.item, c.qty);
      });
    });
  }

  // Intercept cart mutations rather than hooking each add-to-cart entry point.
  var origFetch = window.fetch;
  window.fetch = function () {
    var args = arguments;
    return origFetch.apply(this, args).then(function (res) {
      var url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';

      if (/\/cart\/(add|change|update)(\.js)?(\?|$)/.test(url)) {
        res
          .clone()
          .json()
          .then(function (data) {
            // change/update return the full cart; add.js returns a single line item.
            if (data && Array.isArray(data.items)) {
              handleCartUpdate(data);
            } else {
              fetchCart().then(handleCartUpdate);
            }
          })
          .catch(function () {
            fetchCart().then(handleCartUpdate);
          });
      }

      return res;
    });
  };

  // =============================
  // INIT
  // =============================
  fetchCart()
    .then(function (c) {
      lastCart = c;
    })
    .catch(function () {});

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, trackVariantChange);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', trackPDP);
  } else {
    trackPDP();
  }
})();
